/**
 * Autonomy Earn-Up — the pure trust engine that lets agents EARN autonomy.
 *
 * Today the autonomy matrix (autonomyPolicy.ts) is set by hand and the
 * simulator (policySimulator.ts) previews edits — but nothing closes the
 * loop from *evidence* to *proposal*. This module does exactly that, per
 * action class:
 *
 *   sealed ledger history ──▶ ActionClassStats ──▶ TrustScore ──▶
 *   TierUpgradeProposal (governed, reversible, simulator-consumable)
 *
 * Honesty + safety rules, locked by autonomyEarnUp.test.ts:
 *   • Pure and deterministic: history is passed IN as data; nothing here
 *     reads a store, mutates a policy, or performs any write.
 *   • Evidence-gated: a class is only *eligible* once it has a real sample
 *     (`minSample`), and only *clears* when approvals are high AND
 *     reversals/edits are low. A single ceiling breach suppresses the
 *     upgrade — high approval never papers over reversals.
 *   • Governed: the output is a PROPOSAL envelope (pending, reversible,
 *     evidence-carrying), never an applied change. Committing it routes
 *     through the existing approvals pipeline, and the carried policy
 *     delta is a plain PolicyRule pair — feed `delta.to` to `upsertRule`
 *     and dry-run the candidate with `diffPolicySim` before anyone clicks
 *     Approve.
 *   • Never overrides a human 'block': a blocked class is a deliberate
 *     wall, not a trust problem — no upgrade is ever proposed for it, and
 *     a class already at 'auto' has nothing left to earn.
 */

import {
  resolvePolicy,
  WILDCARD,
  type AutonomyPolicy,
  type PolicyMode,
  type PolicyRule,
} from './autonomyPolicy';
import type { OutcomeResult } from './outcomeVerifier';

// --- Ledger event input --------------------------------------------------------

/** What ultimately happened to one governed action of a class. */
export type EarnUpEvent = 'approve' | 'reject' | 'reverse' | 'edit';

/**
 * One sealed ledger event, reduced to the two fields trust is computed on.
 * Adapt real ledger rows (approvals + reversal entries) to this shape at the
 * call site — the engine never imports a live store.
 */
export interface EarnUpLedgerEntry {
  id: string;
  /** Action class the event belongs to (an action kind, e.g. 'create_task'). */
  actionClass: string;
  event: EarnUpEvent;
  /** Optional ISO timestamp; carried for callers, ignored by the math. */
  at?: string;
}

/**
 * Edit-distance data derived from actionDiff output, keyed by ledger entry
 * id. Passed in as plain data (never read live) so `editDistanceAvg` can
 * reflect how heavily humans had to rewrite the agent's drafts.
 */
export type EditDistanceByEntry = Record<string, number>;

// --- Stats ----------------------------------------------------------------------

/** Per-action-class tally of what history actually says. */
export interface ActionClassStats {
  actionClass: string;
  /** All sealed events for the class (the honest denominator). */
  total: number;
  /** Approved as-proposed. */
  approved: number;
  /** Approved only after human edits. */
  edited: number;
  /** Executed then reversed — the strongest trust signal against. */
  reversed: number;
  /** Mean edit distance across edit events that carry diff data; 0 if none. */
  editDistanceAvg: number;
}

/**
 * Group sealed events by action class and count approvals / edits /
 * reversals. Deterministic: classes are returned sorted by name. Empty
 * ledger → []. Edit distances come only from `diffs` passed in — an edit
 * event with no diff data contributes to `edited` but not to the average.
 */
export function computeActionClassStats(
  ledgerEntries: EarnUpLedgerEntry[],
  diffs: EditDistanceByEntry = {},
): ActionClassStats[] {
  const byClass = new Map<
    string,
    ActionClassStats & { editDistanceSum: number; editDistanceCount: number }
  >();

  for (const entry of ledgerEntries) {
    let bucket = byClass.get(entry.actionClass);
    if (!bucket) {
      bucket = {
        actionClass: entry.actionClass,
        total: 0,
        approved: 0,
        edited: 0,
        reversed: 0,
        editDistanceAvg: 0,
        editDistanceSum: 0,
        editDistanceCount: 0,
      };
      byClass.set(entry.actionClass, bucket);
    }
    bucket.total += 1;
    if (entry.event === 'approve') bucket.approved += 1;
    else if (entry.event === 'edit') {
      bucket.edited += 1;
      const d = diffs[entry.id];
      if (typeof d === 'number') {
        bucket.editDistanceSum += d;
        bucket.editDistanceCount += 1;
      }
    } else if (entry.event === 'reverse') bucket.reversed += 1;
    // 'reject' contributes only to total — it is the absence of trust.
  }

  return [...byClass.values()]
    .sort((a, b) => a.actionClass.localeCompare(b.actionClass))
    .map(({ editDistanceSum, editDistanceCount, ...stats }) => ({
      ...stats,
      editDistanceAvg:
        editDistanceCount === 0 ? 0 : editDistanceSum / editDistanceCount,
    }));
}

// --- Trust scoring ----------------------------------------------------------------

/** Thresholds the earn-up gate applies. All rates are 0–1 fractions. */
export interface EarnUpConfig {
  /** Minimum sealed events before a class is even scoreable. */
  minSample: number;
  /** Acceptance rate (approved + edited, over total) required — inclusive. */
  approvalFloor: number;
  /** Maximum tolerated reversal rate — inclusive ceiling. */
  reversalCeil: number;
  /** Maximum tolerated edit rate — inclusive ceiling. */
  editCeil: number;
}

/** Conservative defaults: 20-event sample, ≥90% accepted, ≤2% reversed, ≤10% edited. */
export const DEFAULT_EARN_UP_CONFIG: EarnUpConfig = {
  minSample: 20,
  approvalFloor: 0.9,
  reversalCeil: 0.02,
  editCeil: 0.1,
};

/** A reversal is the strongest negative signal — weighted hardest. */
export const REVERSAL_PENALTY_WEIGHT = 5;
/** An edit means the draft needed human rework — penalized, but less. */
export const EDIT_PENALTY_WEIGHT = 2;

/** Trust score for one eligible (total ≥ minSample) action class. */
export interface TrustScore {
  actionClass: string;
  /** (approved + edited) / total — an edited approval was still accepted. */
  approvalRate: number;
  /** edited / total. */
  editRate: number;
  /** reversed / total. */
  reversalRate: number;
  /** approvalRate penalized by weighted reversal/edit rates, clamped to [0,1]. */
  score: number;
  // Raw counts ride along so proposals can cite honest evidence strings.
  total: number;
  approved: number;
  edited: number;
  reversed: number;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

// --- Outcome-proven trust (Outcome Contracts + Verifier) ---------------------------

/** How hard the aggregate outcome signal can push a class score (± bound). */
export const OUTCOME_TRUST_WEIGHT = 0.25;
/** A MISSED claim outweighs a VERIFIED one — broken promises cost double. */
export const MISSED_PENALTY_WEIGHT = 2;

/** Aggregate signal derived from sealed outcome verdicts. */
export interface OutcomeTrustSignal {
  /** VERIFIED / total results (INCONCLUSIVE stays in the denominator). */
  verifiedRate: number;
  /** MISSED / total results. */
  missedRate: number;
  /** Bounded score shift in [-OUTCOME_TRUST_WEIGHT, +OUTCOME_TRUST_WEIGHT]. */
  trustDelta: number;
}

/**
 * Distill sealed outcome verdicts into one honest trust signal. INCONCLUSIVE
 * results count in the denominator but earn nothing — an unproven claim
 * never raises trust. All-VERIFIED yields the maximum positive delta;
 * majority-MISSED goes negative (a missed claim is weighted double). Empty
 * input → all zeros, so trust is unchanged when no outcomes exist yet.
 */
export function outcomeTrustSignal(results: OutcomeResult[]): OutcomeTrustSignal {
  const total = results.length;
  if (total === 0) return { verifiedRate: 0, missedRate: 0, trustDelta: 0 };

  let verified = 0;
  let missed = 0;
  for (const r of results) {
    if (r.verdict === 'VERIFIED') verified += 1;
    else if (r.verdict === 'MISSED') missed += 1;
  }
  const verifiedRate = verified / total;
  const missedRate = missed / total;
  const raw = OUTCOME_TRUST_WEIGHT * (verifiedRate - MISSED_PENALTY_WEIGHT * missedRate);
  const trustDelta = Math.min(
    OUTCOME_TRUST_WEIGHT,
    Math.max(-OUTCOME_TRUST_WEIGHT, raw),
  );
  return { verifiedRate, missedRate, trustDelta };
}

/**
 * Score trust per class. Classes under `minSample` are NOT eligible and are
 * omitted entirely — no score is better than a score on noise. Pure and
 * deterministic: same stats + config → same scores, in stats order.
 *
 * `outcomes` (optional, additive) feeds PROVEN results into the score: sealed
 * outcome verdicts shift every eligible class by the bounded
 * {@link outcomeTrustSignal} delta, moving trust toward what actually
 * happened rather than what a human merely approved. Default empty →
 * behavior identical to before the parameter existed.
 */
export function scoreTrust(
  stats: ActionClassStats[],
  cfg: EarnUpConfig = DEFAULT_EARN_UP_CONFIG,
  outcomes: OutcomeResult[] = [],
): TrustScore[] {
  const { trustDelta } = outcomeTrustSignal(outcomes);
  const scores: TrustScore[] = [];
  for (const s of stats) {
    if (s.total < cfg.minSample) continue;
    const approvalRate = (s.approved + s.edited) / s.total;
    const editRate = s.edited / s.total;
    const reversalRate = s.reversed / s.total;
    scores.push({
      actionClass: s.actionClass,
      approvalRate,
      editRate,
      reversalRate,
      score: clamp01(
        approvalRate -
          REVERSAL_PENALTY_WEIGHT * reversalRate -
          EDIT_PENALTY_WEIGHT * editRate +
          trustDelta,
      ),
      total: s.total,
      approved: s.approved,
      edited: s.edited,
      reversed: s.reversed,
    });
  }
  return scores;
}

// --- Tier-upgrade proposals ----------------------------------------------------------

/** Hours a human has to veto an auto-executed action of an earned-up class. */
export const VETO_WINDOW_HOURS = 2;

/** The policy delta a proposal carries: current rule → candidate rule. */
export interface PolicyDelta {
  /** Rule currently governing the class (resolved; may be the 'ask' default). */
  from: PolicyRule;
  /** Candidate rule — feed to `upsertRule` and dry-run via policySimulator. */
  to: PolicyRule;
}

/**
 * A governed, reversible tier-upgrade proposal — the same envelope shape as
 * every agent proposal (stable id + pending status + human-readable note),
 * carrying the evidence and the simulator-consumable policy delta. Emitting
 * one changes NOTHING: it must clear the existing approval queue, and the
 * carried delta stays reversible (re-upsert `delta.from` to roll back).
 */
export interface TierUpgradeProposal {
  /** Deterministic id so re-running the engine proposes the same upgrade. */
  id: string;
  origin: 'autonomy_earn_up';
  status: 'pending';
  actionClass: string;
  /** Current autonomy tier the class resolves to. */
  currentTier: PolicyMode;
  /** Proposed next tier. */
  proposedTier: PolicyMode;
  /** Human-readable next-tier label, e.g. "auto-send w/ 2h veto". */
  proposedTierLabel: string;
  /** Honest evidence string, e.g. "47/50 approved, 0 reversals". */
  evidence: string;
  /** Current auto-approve $ ceiling (null = none / not applicable). */
  currentThreshold: number | null;
  /** Proposed auto-approve $ ceiling under the new tier. */
  proposedThreshold: number | null;
  /** Always true: the delta can be rolled back by restoring `delta.from`. */
  reversible: true;
  /** Simulator-consumable current→candidate rule pair. */
  delta: PolicyDelta;
  /** Provenance note for the approvals queue. */
  note: string;
}

/** Plural-safe evidence line: 'X/Y approved, N reversals'. */
function evidenceLine(score: TrustScore): string {
  const accepted = score.approved + score.edited;
  const n = score.reversed;
  return `${accepted}/${score.total} approved, ${n} reversal${n === 1 ? '' : 's'}`;
}

/**
 * For every eligible class that clears ALL floors/ceilings, compile the
 * governed proposal recommending the next autonomy tier. Only 'ask' has a
 * next tier ('auto' with a veto window); 'auto' has nothing left to earn
 * and 'block' is a deliberate human wall this engine never argues with.
 * Pure: no writes, and `currentPolicy` is never mutated.
 */
export function proposeTierUpgrades(
  scores: TrustScore[],
  currentPolicy: AutonomyPolicy,
  cfg: EarnUpConfig = DEFAULT_EARN_UP_CONFIG,
): TierUpgradeProposal[] {
  const proposals: TierUpgradeProposal[] = [];

  for (const score of scores) {
    if (score.approvalRate < cfg.approvalFloor) continue;
    if (score.reversalRate > cfg.reversalCeil) continue;
    if (score.editRate > cfg.editCeil) continue;

    // The class-wide rule ('*' agent) governs the earn-up: trust was earned
    // by the action class across history, so the upgrade is class-scoped.
    const from = resolvePolicy(currentPolicy, WILDCARD, score.actionClass);
    if (from.mode !== 'ask') continue; // 'auto' earned already; 'block' is a wall.

    const to: PolicyRule = {
      agentId: WILDCARD,
      actionType: score.actionClass,
      mode: 'auto',
      maxImpact: from.maxImpact,
    };
    const evidence = evidenceLine(score);

    proposals.push({
      id: `earnup-${score.actionClass}`,
      origin: 'autonomy_earn_up',
      status: 'pending',
      actionClass: score.actionClass,
      currentTier: from.mode,
      proposedTier: to.mode,
      proposedTierLabel: `auto-send w/ ${VETO_WINDOW_HOURS}h veto`,
      evidence,
      currentThreshold: from.maxImpact,
      proposedThreshold: to.maxImpact,
      reversible: true,
      delta: { from, to },
      note: `Autonomy earn-up: ${score.actionClass} ask → auto (${evidence})`,
    });
  }

  return proposals;
}
